import { useEffect, useMemo, useState } from "react";
import { Contatto, LeadInteraction } from "../../../types/index";
import { useAuth } from "../../../context/AuthContext";
import { apiFetch } from "../../../apiClient";
import { useSettings } from "../../../context/SettingsContext";
import { normalizeSelectOptions, withCurrentOption } from "../../../utils/normalizeSelectOptions";
import { effectiveNumeroContatti } from "../../../utils/contactCounts";
import AppSelect from "../AppSelect";
import { formInputClass, formTextareaClass } from "../FormModal";

interface DettagliContattoModalProps {
  contatto: Contatto;
  onClose: () => void;
  onSave: (updated: Contatto) => void;
}

type Draft = {
  nome: string;
  cognome: string;
  azienda: string;
  settore: string;
  indirizzo: string;
  linkedin_url: string;
  telefono: string;
  email: string;
  associato: string;
  servizio_proposto: string;
  esito_contatto: string;
  commento: string;
  analisi_lead: string;
};

type OptionItem = { value: string; label: string };

const emptyToNull = (value: string) => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const formatDate = (value?: string) => {
  if (!value) return "Mai";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("it-IT");
};

export default function DettagliContattoModal({ contatto, onClose, onSave }: DettagliContattoModalProps) {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [numeroContatti, setNumeroContatti] = useState<number>(() =>
    effectiveNumeroContatti(contatto.numero_contatti, contatto.data_ultimo_contatto)
  );
  const [dataUltimoContatto, setDataUltimoContatto] = useState<string | undefined>(contatto.data_ultimo_contatto);
  const [pendingSaveConfirm, setPendingSaveConfirm] = useState(false);
  const [interactions, setInteractions] = useState<LeadInteraction[]>([]);
  const [loadingInteractions, setLoadingInteractions] = useState(false);
  const [interactionNote, setInteractionNote] = useState("");
  const { associati: settingsAssociati, servizi: settingsServizi, esiti: settingsEsiti } =
    useSettings();

  const options = useMemo(
    () => ({
      associati: normalizeSelectOptions(settingsAssociati),
      servizi: normalizeSelectOptions(settingsServizi),
      esiti: normalizeSelectOptions(settingsEsiti),
    }),
    [settingsAssociati, settingsServizi, settingsEsiti]
  );

  const isAdmin = user?.role === 'admin';
  const labelTipo = useMemo(
    () =>
      ({
        contatto: "LinkedIn",
        "small-lead": "Small Lead",
        startup: "Start-up",
      }[contatto.tipo]),
    [contatto.tipo]
  );

  const [draft, setDraft] = useState<Draft>({
    nome: contatto.nome || "",
    cognome: contatto.cognome || "",
    azienda: contatto.azienda || "",
    settore: contatto.settore || "",
    indirizzo: contatto.indirizzo || "",
    linkedin_url: contatto.linkedin_url || "",
    telefono: contatto.telefono || "",
    email: contatto.email || "",
    associato: contatto.associato || "",
    servizio_proposto: contatto.servizio_proposto || "",
    esito_contatto: contatto.esito_contatto || "",
    commento: contatto.commento || "",
    analisi_lead: contatto.analisi_lead || "",
  });

  useEffect(() => {
    setNumeroContatti(
      effectiveNumeroContatti(contatto.numero_contatti, contatto.data_ultimo_contatto)
    );
    setDataUltimoContatto(contatto.data_ultimo_contatto);
    setInteractionNote("");
    setPendingSaveConfirm(false);
  }, [contatto.id, contatto.tipo, contatto.numero_contatti, contatto.data_ultimo_contatto]);

  const loadInteractions = async () => {
    if (!user) return;

    try {
      setLoadingInteractions(true);
      const res = await apiFetch(
        `contatti/${contatto.id}/interactions?tipo=${encodeURIComponent(contatto.tipo)}&limit=50&offset=0`
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || "Errore caricamento storico");
      setInteractions(Array.isArray(body.interactions) ? body.interactions : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore caricamento storico");
    } finally {
      setLoadingInteractions(false);
    }
  };

  useEffect(() => {
    loadInteractions();
  }, [contatto.id, contatto.tipo]);

  const nomePrincipale =
    contatto.tipo === "startup"
      ? draft.azienda || `${draft.nome} ${draft.cognome}`.trim() || "Lead senza nome"
      : `${draft.nome} ${draft.cognome}`.trim() || draft.azienda || "Lead senza nome";

  const updateDraft = (key: keyof Draft, value: string) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const buildUpdatePayload = () => {
    const original: Draft = {
      nome: contatto.nome || "",
      cognome: contatto.cognome || "",
      azienda: contatto.azienda || "",
      settore: contatto.settore || "",
      indirizzo: contatto.indirizzo || "",
      linkedin_url: contatto.linkedin_url || "",
      telefono: contatto.telefono || "",
      email: contatto.email || "",
      associato: contatto.associato || "",
      servizio_proposto: contatto.servizio_proposto || "",
      esito_contatto: contatto.esito_contatto || "",
      commento: contatto.commento || "",
      analisi_lead: contatto.analisi_lead || "",
    };

    const changed = (key: keyof Draft) => draft[key] !== original[key];
    const data: Record<string, string | null> = {};

    if (contatto.tipo === "contatto") {
      if (changed("nome")) data.first_name = emptyToNull(draft.nome);
      if (changed("cognome")) data.last_name = emptyToNull(draft.cognome);
      if (changed("azienda")) data.company_name = emptyToNull(draft.azienda);
      if (changed("settore")) data.occupation = emptyToNull(draft.settore);
      if (changed("indirizzo")) data.location = emptyToNull(draft.indirizzo);
      if (changed("linkedin_url")) data.linkedin_url = emptyToNull(draft.linkedin_url);
      if (isAdmin && changed("associato")) data.associato = emptyToNull(draft.associato);
      if (changed("servizio_proposto")) data.servizio_proposto = emptyToNull(draft.servizio_proposto);
      if (changed("esito_contatto")) data.esito_contatto = emptyToNull(draft.esito_contatto);
      if (changed("analisi_lead")) data.analisi_lead = emptyToNull(draft.analisi_lead);
    }

    if (contatto.tipo === "small-lead") {
      if (changed("nome")) data.nome = emptyToNull(draft.nome);
      if (changed("settore")) data.settore_specifico = emptyToNull(draft.settore);
      if (changed("indirizzo")) data.indirizzo = emptyToNull(draft.indirizzo);
      if (changed("telefono")) data.telefono = emptyToNull(draft.telefono);
      if (changed("email")) data.email = emptyToNull(draft.email);
      if (isAdmin && changed("associato")) data.responsabile_jevis = emptyToNull(draft.associato);
      if (changed("servizio_proposto")) data.servizio_proposto = emptyToNull(draft.servizio_proposto);
      if (changed("esito_contatto")) data.esito_contatto = emptyToNull(draft.esito_contatto);
      if (changed("commento")) data.commento = emptyToNull(draft.commento);
      if (changed("analisi_lead")) data.analisi_lead = emptyToNull(draft.analisi_lead);
    }

    if (contatto.tipo === "startup") {
      if (changed("azienda")) data.company_name = emptyToNull(draft.azienda);
      if (changed("nome")) data.first_name = emptyToNull(draft.nome);
      if (changed("cognome")) data.last_name = emptyToNull(draft.cognome);
      if (changed("settore")) data.occupation = emptyToNull(draft.settore);
      if (changed("indirizzo")) data.location = emptyToNull(draft.indirizzo);
      if (changed("linkedin_url")) data.linkedin_url = emptyToNull(draft.linkedin_url);
      if (changed("telefono")) data.telefono = emptyToNull(draft.telefono);
      if (isAdmin && changed("associato")) data.saler = emptyToNull(draft.associato);
      if (changed("servizio_proposto")) data.servizio_proposto = emptyToNull(draft.servizio_proposto);
      if (changed("esito_contatto")) data.esito_contatto = emptyToNull(draft.esito_contatto);
      if (changed("analisi_lead")) data.analisi_lead = emptyToNull(draft.analisi_lead);
    }

    return data;
  };

  const hasDraftChanges = () => Object.keys(buildUpdatePayload()).length > 0;

  const handleSaveRequest = () => {
    setError("");
    if (!draft.analisi_lead.trim()) {
      setError("Il link all'analisi è obbligatorio per registrare un contatto");
      return;
    }
    if (!hasDraftChanges()) {
      setIsEditing(false);
      setPendingSaveConfirm(false);
      return;
    }
    setPendingSaveConfirm(true);
  };

  const handleSaveConfirm = async () => {
    if (!user) {
      setError("Sessione non valida");
      return;
    }

    if (!draft.analisi_lead.trim()) {
      setError("Il link all'analisi è obbligatorio per registrare un contatto");
      setPendingSaveConfirm(false);
      return;
    }

    const payload = buildUpdatePayload();
    if (Object.keys(payload).length === 0) {
      setIsEditing(false);
      setPendingSaveConfirm(false);
      return;
    }

    try {
      setSaving(true);
      setError("");

      const res = await apiFetch(`contatti/update_lead?lead_id=${contatto.id}&tipo=${contatto.tipo}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || "Errore durante il salvataggio");
      }

      const punteggioRes = await apiFetch(
        `contatti/${contatto.id}/punteggio/?tipo=${encodeURIComponent(contatto.tipo)}&operazione=%2B`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ note: interactionNote.trim() || null }),
        }
      );
      const punteggioBody = await punteggioRes.json().catch(() => ({}));
      if (!punteggioRes.ok) {
        throw new Error(punteggioBody.detail || "Errore registrazione interazione");
      }

      const nuovoNumero = effectiveNumeroContatti(
        punteggioBody.nuovo_punteggio ?? numeroContatti + 1,
        punteggioBody.data_ultimo_contatto ?? dataUltimoContatto
      );
      const nuovaData =
        punteggioBody.data_ultimo_contatto !== undefined
          ? punteggioBody.data_ultimo_contatto || undefined
          : new Date().toISOString().slice(0, 10);

      setNumeroContatti(nuovoNumero);
      setDataUltimoContatto(nuovaData);
      await loadInteractions();

      onSave({
        ...contatto,
        ...draft,
        numero_contatti: nuovoNumero,
        data_ultimo_contatto: nuovaData,
        nome: draft.nome || undefined,
        cognome: draft.cognome || undefined,
        azienda: draft.azienda || undefined,
        settore: draft.settore || undefined,
        indirizzo: draft.indirizzo || undefined,
        linkedin_url: draft.linkedin_url || undefined,
        telefono: draft.telefono || undefined,
        email: draft.email || undefined,
        associato: draft.associato || undefined,
        servizio_proposto: draft.servizio_proposto || undefined,
        esito_contatto: draft.esito_contatto || undefined,
        commento: draft.commento || undefined,
        analisi_lead: draft.analisi_lead || undefined,
      });
      setInteractionNote("");
      setPendingSaveConfirm(false);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Errore imprevisto");
    } finally {
      setSaving(false);
    }
  };

  const SelectField = ({
    value,
    field,
    choices,
  }: {
    value: string;
    field: keyof Draft;
    choices: OptionItem[];
  }) => {
    const selectOptions = withCurrentOption(choices, value);
    return (
      <div className="mt-1">
        <AppSelect
          options={selectOptions}
          value={selectOptions.find((c) => c.value === value) ?? null}
          onChange={(opt) => updateDraft(field, opt?.value ?? "")}
          placeholder="Seleziona..."
          isClearable
          isSearchable
          size="sm"
          menuZIndex={10050}
        />
      </div>
    );
  };

  const Field = ({
    label,
    value,
    field,
    textarea = false,
  }: {
    label: string;
    value: string;
    field: keyof Draft;
    textarea?: boolean;
  }) => (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
      {isEditing ? (
        textarea ? (
          <textarea
            value={value}
            onChange={(e) => updateDraft(field, e.target.value)}
            className={formTextareaClass}
            rows={4}
          />
        ) : (
          <input
            value={value}
            onChange={(e) => updateDraft(field, e.target.value)}
            className={formInputClass}
          />
        )
      ) : (
        <p className="min-h-6 text-sm font-semibold text-secondary/90">{value || "—"}</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#1e2d4a]/50 p-4 backdrop-blur-sm">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-white/20 bg-white shadow-2xl">
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-[#2e436a] via-[#3d5280] to-[#1e2d4a] px-6 py-5 text-white">
          <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-[#cd212a]/25 blur-2xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{labelTipo}</p>
              <h2 className="text-2xl font-extrabold leading-tight tracking-tight">{nomePrincipale}</h2>
            </div>
            <div className="flex shrink-0 gap-2">
              {isEditing && (
                <button
                  type="button"
                  onClick={() => {
                    setDraft({
                      nome: contatto.nome || "",
                      cognome: contatto.cognome || "",
                      azienda: contatto.azienda || "",
                      settore: contatto.settore || "",
                      indirizzo: contatto.indirizzo || "",
                      linkedin_url: contatto.linkedin_url || "",
                      telefono: contatto.telefono || "",
                      email: contatto.email || "",
                      associato: contatto.associato || "",
                      servizio_proposto: contatto.servizio_proposto || "",
                      esito_contatto: contatto.esito_contatto || "",
                      commento: contatto.commento || "",
                      analisi_lead: contatto.analisi_lead || "",
                    });
                    setInteractionNote("");
                    setPendingSaveConfirm(false);
                    setIsEditing(false);
                    setError("");
                  }}
                  className="rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-white/20"
                >
                  Annulla
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white transition hover:bg-white/20"
                aria-label="Chiudi"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex flex-col gap-6 text-left">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/45">Assegnato a</p>
              {isEditing && isAdmin ? (
                <SelectField value={draft.associato} field="associato" choices={options.associati} />
              ) : (
                <p className="mt-1 text-sm font-bold text-secondary">{draft.associato || "Non assegnato"}</p>
              )}
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/45">Servizio</p>
              {isEditing ? (
                <SelectField value={draft.servizio_proposto} field="servizio_proposto" choices={options.servizi} />
              ) : (
                <p className="mt-1 text-sm font-bold text-secondary">{draft.servizio_proposto || "—"}</p>
              )}
            </div>
            <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/45">Esito</p>
              {isEditing ? (
                <SelectField value={draft.esito_contatto} field="esito_contatto" choices={options.esiti} />
              ) : (
                <p className="mt-1 text-sm font-bold text-secondary">{draft.esito_contatto || "—"}</p>
              )}
            </div>
          </div>

          {contatto.tipo === "contatto" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome" value={draft.nome} field="nome" />
              <Field label="Cognome" value={draft.cognome} field="cognome" />
              <Field label="Azienda" value={draft.azienda} field="azienda" />
              <Field label="Settore / Ruolo" value={draft.settore} field="settore" />
              <Field label="Indirizzo" value={draft.indirizzo} field="indirizzo" />
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">LinkedIn</p>
                {isEditing ? (
                  <input
                    value={draft.linkedin_url}
                    onChange={(e) => updateDraft("linkedin_url", e.target.value)}
                    className={formInputClass}
                  />
                ) : (
                  <a
                    href={draft.linkedin_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className={`inline-flex rounded-lg px-3 py-2 text-sm font-semibold ${
                      draft.linkedin_url
                        ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
                        : "bg-gray-100 text-gray-400 pointer-events-none"
                    }`}
                  >
                    Apri profilo LinkedIn
                  </a>
                )}
              </div>
            </div>
          )}

          {contatto.tipo === "small-lead" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome lead" value={draft.nome} field="nome" />
              <Field label="Settore specifico" value={draft.settore} field="settore" />
              <Field label="Indirizzo" value={draft.indirizzo} field="indirizzo" />
              <Field label="Telefono" value={draft.telefono} field="telefono" />
              <Field label="Email" value={draft.email} field="email" />
              <Field label="Commento" value={draft.commento} field="commento" textarea />
            </div>
          )}

          {contatto.tipo === "startup" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Azienda" value={draft.azienda} field="azienda" />
              <Field label="Nome referente" value={draft.nome} field="nome" />
              <Field label="Cognome referente" value={draft.cognome} field="cognome" />
              <Field label="Settore / Ruolo" value={draft.settore} field="settore" />
              <Field label="Indirizzo" value={draft.indirizzo} field="indirizzo" />
              <Field label="Telefono" value={draft.telefono} field="telefono" />
              <div className="space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">LinkedIn</p>
                {isEditing ? (
                  <input
                    value={draft.linkedin_url}
                    onChange={(e) => updateDraft("linkedin_url", e.target.value)}
                    className={formInputClass}
                  />
                ) : (
                  <a
                    href={draft.linkedin_url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    className={`inline-flex rounded-lg px-3 py-2 text-sm font-semibold ${
                      draft.linkedin_url
                        ? "bg-blue-50 text-blue-700 hover:bg-blue-100"
                        : "bg-gray-100 text-gray-400 pointer-events-none"
                    }`}
                  >
                    Apri profilo LinkedIn
                  </a>
                )}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/45">
              Link analisi <span className="text-[#cd212a]">*</span>
            </p>
            {isEditing ? (
              <input
                value={draft.analisi_lead}
                onChange={(e) => updateDraft("analisi_lead", e.target.value)}
                placeholder="https://..."
                required
                className={`mt-1 ${formInputClass}`}
              />
            ) : (
              <a
                href={draft.analisi_lead || "#"}
                target="_blank"
                rel="noreferrer"
                className={`mt-2 inline-flex rounded-xl px-3 py-2 text-sm font-semibold transition ${
                  draft.analisi_lead
                    ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-100"
                    : "bg-slate-100 text-gray-400 pointer-events-none"
                }`}
              >
                Apri analisi lead
              </a>
            )}
            {isEditing && (
              <div className="mt-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">Nota interazione</p>
                <textarea
                  value={interactionNote}
                  onChange={(e) => setInteractionNote(e.target.value)}
                  rows={2}
                  placeholder="Nota opzionale per lo storico interazioni"
                  className={formTextareaClass}
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 border-t border-slate-100 pt-4 sm:grid-cols-2">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/45">Numero contatti</p>
              <div className="mt-2">
                <span className="inline-flex h-11 min-w-11 items-center justify-center rounded-xl bg-secondary/10 px-3 text-lg font-extrabold tabular-nums text-secondary">
                  {numeroContatti}
                </span>
                {isEditing && (
                  <p className="mt-2 text-[11px] text-gray-500">
                    Si aggiorna automaticamente al salvataggio delle modifiche.
                  </p>
                )}
              </div>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/45">
                Ultimo contatto{interactions.length > 0 && !dataUltimoContatto ? " (nuovo ciclo)" : ""}
              </p>
              <p className="text-sm font-bold text-secondary">{formatDate(dataUltimoContatto)}</p>
              {interactions.length > 0 && !dataUltimoContatto && (
                <p className="mt-1 text-xs text-secondary/70">
                  I contatti precedenti sono nello storico qui sotto.
                </p>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200/80 bg-slate-50/80 p-4">
            <p className="text-[11px] font-bold uppercase tracking-wider text-secondary/45">Storico interazioni</p>
            {loadingInteractions ? (
              <p className="mt-2 text-sm text-gray-500">Caricamento storico...</p>
            ) : interactions.length === 0 ? (
              <p className="mt-2 text-sm text-gray-500">
                Nessuna interazione registrata.
                {!dataUltimoContatto && (contatto.numero_contatti ?? 0) === 0
                  ? " Salva una modifica per registrare il primo contatto nello storico."
                  : ""}
              </p>
            ) : (
              <div
                className={`mt-3 space-y-2 pr-1 ${
                  interactions.length > 4 ? "max-h-[22rem] overflow-y-auto" : ""
                }`}
              >
                {interactions.map((item) => (
                  <div key={item.id} className="rounded-xl border border-slate-200/80 bg-white p-3">
                    <p className="text-xs font-semibold text-secondary">
                      {formatDate(item.interaction_date)} {item.created_by ? `- ${item.created_by}` : ""}
                    </p>
                    <p className="text-xs text-gray-500">
                      Servizio: {item.servizio_proposto || "—"} | Esito: {item.esito_contatto || "—"}
                    </p>
                    {item.note && <p className="mt-1 text-sm text-gray-700">{item.note}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-600">
              {error}
            </div>
          )}
          {!isAdmin && isEditing && (
            <p className="text-xs text-amber-700">
              L'assegnazione lead puo essere modificata solo dagli admin.
            </p>
          )}
          {isEditing && pendingSaveConfirm && (
            <div className="animate-fade-in rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-4 shadow-sm">
              <p className="text-sm font-semibold text-emerald-900">Confermi il salvataggio?</p>
              <p className="mt-1 text-xs text-emerald-800/90">
                Sono state rilevate modifiche rispetto alla scheda attuale. Verranno salvate, il numero
                contatti aumenterà di 1 e verrà aggiunta una voce allo storico interazioni.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSaveConfirm}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 px-4 py-2 text-xs font-extrabold uppercase tracking-wide text-white shadow-md shadow-emerald-500/25 transition hover:shadow-lg disabled:opacity-60"
                >
                  {saving ? "Salvataggio..." : "Conferma salvataggio"}
                </button>
                <button
                  type="button"
                  onClick={() => setPendingSaveConfirm(false)}
                  disabled={saving}
                  className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-bold text-gray-600 transition hover:bg-gray-50 disabled:opacity-60"
                >
                  Annulla
                </button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-100 bg-slate-50/50 px-6 py-4 -mx-6 -mb-6 mt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-secondary transition hover:bg-slate-50"
            >
              Chiudi
            </button>
            {isEditing && !pendingSaveConfirm && (
              <button
                type="button"
                onClick={handleSaveRequest}
                disabled={saving || !hasDraftChanges()}
                className="rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-primary/90 disabled:opacity-60"
              >
                Salva modifiche
              </button>
            )}
            {!isEditing && (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="rounded-xl bg-secondary px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-secondary/90"
              >
                Modifica
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}